import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { blue, blueGrey, grey, orange } from "@mui/material/colors";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CheckOutlined, CloseOutlined, EditOutlined, PolicyOutlined } from "@mui/icons-material";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { formatDateTime } from "../utils/functions/formatDateTime";
import { getUserFullNameFromEmail } from "../../utils/functions/getUserFullNameFromEmail";
import EnhancedCommentAttachmentPreview from "./EnhancedCommentAttachmentPreview";
import { Tooltip } from "@mui/material";
import HtmlEditor from "../../components/html/HtmlEditor";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../redux/actions/sidebarActions";
import { loanDrilldownSidebarTypes } from "../../constants/enums/loanDrilldownScreenSidebarOptions";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../redux/actions/loanDrilldownActions";

const EnhancedCommentItem = ({
  createdDate,
  createdBy,
  comment,
  htmlString,
  loan,
  px = 1,
}) => {
  const user = useSelector((state) => state.user);
  const users = useSelector((state) => state?.users);
  const dispatch = useDispatch();
  const { updateDealComment } = useUnderwritingHook();

  const [editing, setEditing] = useState(false);
  const [draftHtml, setDraftHtml] = useState(null);
  const [saving, setSaving] = useState(false);

  const currentUserEmail = useMemo(
    () => user?.emailAddress,
    [user?.emailAddress],
  );

  const commentId = comment?._id;
  const loanId = loan?._id;

  // console.log("---------------------", comment);
  const userNameFromEmail = useMemo(() => {
    return getUserFullNameFromEmail(users, createdBy);
  }, [createdBy, users]);

  const isSelf = useMemo(() => {
    return currentUserEmail === createdBy;
  }, [currentUserEmail, createdBy]);

  // Comments fanned out from a loan exception carry the exception id under
  // associations.managementExceptions — surface them with a yellow body.
  const isException = useMemo(
    () => !isEmpty(comment?.associations?.managementExceptions),
    [comment?.associations?.managementExceptions],
  );

  // TEMP: comment editing is disabled. Flip COMMENT_EDIT_ENABLED back to true to
  // restore the inline edit pencil (POST /underwriting/update_comment/{dealId}/{commentId}).
  const COMMENT_EDIT_ENABLED = false;
  // Only the author can edit, and only when we can resolve both ids needed.
  const canEdit =
    COMMENT_EDIT_ENABLED && isSelf && !!commentId && !!loanId;

  const handleStartEdit = () => {
    setDraftHtml(htmlString);
    setEditing(true);
  };
  const handleCancel = () => {
    setEditing(false);
    setDraftHtml(null);
  };
  const handleSave = () => {
    const nextHtml = draftHtml != null ? draftHtml : htmlString;
    setSaving(true);
    updateDealComment({
      loanId,
      commentId,
      data: { htmlString: nextHtml },
      onSuccessFn: (updatedLoan) => {
        dispatch(loanDrilldownSet(updatedLoan));
        setEditing(false);
        setDraftHtml(null);
      },
      onCompleteFn: () => setSaving(false),
      onFailFn: () => setSaving(false),
    });
  };

  const styles = useMemo(() => {
    const margin = 4;

    return {
      mr: isSelf ? 0 : margin,
      ml: isSelf ? margin : 0,
      flexGrow: 1,
      justifyContent: isSelf ? "flex-end" : "flex-start",
      gap: 0.8,
    };
  }, [isSelf]);
  return (
    <Flx sx={styles}>
      {isSelf ? null : <UserAvatar isSelf={isSelf} userEmail={createdBy} />}
      <Flx gap={0.5} column sx={{ overflow: "hidden" }}>
        <Flx
          gap={0.5}
          ac
          fw
          wrap
          sx={{ justifyContent: isSelf ? "flex-end" : "flex-start" }}
        >
          <Tooltip title={createdBy}>
            <>
              <CreatedByTitle>{userNameFromEmail}</CreatedByTitle>
            </>
          </Tooltip>
          <CreatedTime>{createdDate}</CreatedTime>
          {comment?.commentUpdated && (
            <Txt sx={{ fontSize: "10px", opacity: 0.5, fontStyle: "italic" }}>
              (edited)
            </Txt>
          )}
          {canEdit && !editing && (
            <Tooltip title="Edit comment">
              <IconButton
                size="small"
                onClick={handleStartEdit}
                sx={{ p: 0.25, opacity: 0.6, "&:hover": { opacity: 1 } }}
              >
                <EditOutlined sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Flx>
        <CommentBody
          topContent={
            <>
              <ExceptionLineageBadge comment={comment} loan={loan} />
              <FromExceptionBadge comment={comment} loan={loan} />
            </>
          }
          attachmentsContent={
            <EnhancedCommentAttachmentPreview
              loan={loan}
              //
              // associations={comment?.associations}
              loanDocumentIds={comment?.associations?.loanDocuments}
              borrowerIds={comment?.associations?.borrowers}
              subjectPropertyIds={comment?.associations?.subjectProperties}
              //
              disableDelete
            />
          }
          isSelf={isSelf}
          isException={isException}
          editing={editing}
          onChangeDraft={(html) => setDraftHtml(html)}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={saving}
        >
          {htmlString}
        </CommentBody>
      </Flx>
      {isSelf ? <UserAvatar isSelf={isSelf} userEmail={createdBy} /> : null}
    </Flx>
  );
};

/**
 * Exception-comment lineage chip. A comment authored on a loan exception with
 * "Also post to the loan's main comment thread" checked is written atomically by
 * the backend with its exception lineage attached. On the MAIN thread we surface
 * that lineage as a small "Exception · {category}" chip that deep-links back to
 * the source exception's drawer.
 *
 * Detection (per the backend lineage contract):
 *   comment.commentType === 'exceptions'  OR  associations.exceptions[] present.
 * Lineage id lives on `associations.exceptions[0]` (legacy records used
 * `commentObjects.exceptions`); we resolve it against `loan.exceptions` (join on
 * `_id`) for the category label and click-through.
 */
const ExceptionLineageBadge = ({ comment, loan }) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  // Lineage id array — new key first, legacy fallback second.
  const exceptionIds =
    comment?.associations?.exceptions ?? comment?.commentObjects?.exceptions ?? [];

  const isExceptionLineage =
    comment?.commentType === "exceptions" ||
    (isArray(exceptionIds) && exceptionIds.length > 0);

  const exception = useMemo(() => {
    if (!isArray(exceptionIds) || isEmpty(exceptionIds)) return null;
    const exceptionId = exceptionIds[0];
    return loan?.exceptions?.find((e) => e?._id === exceptionId) || null;
  }, [exceptionIds, loan?.exceptions]);

  // Show the chip whenever the comment carries exception lineage, even if the
  // exception object can't be resolved (still signals origin); prefer the richer
  // category label/tooltip when we have the object.
  if (!isExceptionLineage) return null;

  const label = exception?.exceptionCategory
    ? `Exception · ${exception.exceptionCategory}`
    : "Exception";

  // Clickable only when we can resolve the exception — opens the same edit
  // drawer a row-click in the exceptions table would (ADD_EXCEPTION sidebar +
  // the exception object as sidebar state).
  const handleClick = (e) => {
    e.stopPropagation();
    if (!exception) return;
    dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EXCEPTION));
    dispatch(sidebarSetStateValue(exception));
  };

  const tooltip = exception ? (
    <Flx column gap={0.25}>
      <Txt sx={{ fontSize: "11px", fontWeight: 600 }}>
        From exception — click to open
      </Txt>
      {exception?.exceptionCategory ? (
        <Txt sx={{ fontSize: "11px" }}>
          Category: {exception.exceptionCategory}
        </Txt>
      ) : null}
      {exception?.exceptionStatus ? (
        <Txt sx={{ fontSize: "11px" }}>Status: {exception.exceptionStatus}</Txt>
      ) : null}
      {exception?.exceptionDescription ? (
        <Txt sx={{ fontSize: "11px" }}>{exception.exceptionDescription}</Txt>
      ) : null}
    </Flx>
  ) : (
    "From exception"
  );

  return (
    <Tooltip title={tooltip}>
      {/* Box (not Flx) so MUI Tooltip has a ref-able child to anchor to. */}
      <Box
        onClick={exception ? handleClick : undefined}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.4,
          mb: 1,
          px: 0.7,
          py: 0.25,
          borderRadius: "6px",
          backgroundColor: orange[100],
          border: `1px solid ${orange[200]}`,
          maxWidth: "100%",
          cursor: exception ? "pointer" : "default",
          transition: "background-color 120ms",
          "&:hover": exception ? { backgroundColor: orange[200] } : undefined,
        }}
      >
        <PolicyOutlined sx={{ fontSize: "12px", color: orange[800] }} />
        <Txt
          sx={{
            fontSize: "10px",
            fontWeight: 600,
            color: theme.palette.text.secondary,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Txt>
      </Box>
    </Tooltip>
  );
};

/**
 * Renders a small "From exception" badge when a comment was fanned out from a
 * loan exception. The exception id rides on the comment as
 * `associations.managementExceptions[0]`; we resolve it against
 * `loan.exceptions` so the badge can surface exception data (category, status)
 * and link the loan-stream comment back to its source exception.
 */
const FromExceptionBadge = ({ comment, loan }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const exceptionIds = comment?.associations?.managementExceptions;

  const exception = useMemo(() => {
    if (!isArray(exceptionIds) || isEmpty(exceptionIds)) return null;
    const exceptionId = exceptionIds[0];
    return loan?.exceptions?.find((e) => e?._id === exceptionId) || null;
  }, [exceptionIds, loan?.exceptions]);

  // Show the badge even if the exception can't be resolved (still signals
  // origin), but prefer the richer label/tooltip when we have the object.
  if (!isArray(exceptionIds) || isEmpty(exceptionIds)) return null;

  const label = exception?.exceptionCategory
    ? `Exception · ${exception.exceptionCategory}`
    : "Exception";

  // Clickable only when we can resolve the exception — opens the same edit
  // sidebar a row-click in the exceptions table would (ADD_EXCEPTION + the
  // exception object as sidebar state).
  const handleClick = (e) => {
    e.stopPropagation();
    if (!exception) return;
    dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EXCEPTION));
    dispatch(sidebarSetStateValue(exception));
  };

  const tooltip = exception ? (
    <Flx column gap={0.25}>
      <Txt sx={{ fontSize: "11px", fontWeight: 600 }}>
        From exception — click to edit
      </Txt>
      {exception?.exceptionCategory ? (
        <Txt sx={{ fontSize: "11px" }}>
          Category: {exception.exceptionCategory}
        </Txt>
      ) : null}
      {exception?.exceptionStatus ? (
        <Txt sx={{ fontSize: "11px" }}>Status: {exception.exceptionStatus}</Txt>
      ) : null}
      {exception?.exceptionDescription ? (
        <Txt sx={{ fontSize: "11px" }}>{exception.exceptionDescription}</Txt>
      ) : null}
    </Flx>
  ) : (
    "From exception"
  );

  return (
    <Tooltip title={tooltip}>
      {/* Box (not Flx) so MUI Tooltip has a ref-able child to anchor to. */}
      <Box
        onClick={exception ? handleClick : undefined}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.4,
          mb: 1,
          px: 0.7,
          py: 0.25,
          borderRadius: "6px",
          backgroundColor: orange[100],
          border: `1px solid ${orange[200]}`,
          maxWidth: "100%",
          cursor: exception ? "pointer" : "default",
          transition: "background-color 120ms",
          "&:hover": exception ? { backgroundColor: orange[200] } : undefined,
        }}
      >
        <PolicyOutlined sx={{ fontSize: "12px", color: orange[800] }} />
        <Txt
          sx={{
            fontSize: "10px",
            fontWeight: 600,
            color: theme.palette.text.secondary,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Txt>
      </Box>
    </Tooltip>
  );
};

const CreatedByTitle = ({ children }) => {
  const styles = useMemo(() => {
    return {
      fontSize: "11px",
      fontWeight: 400,
      opacity: 0.7,
    };
  }, []);
  return <Txt sx={styles}>{children}</Txt>;
};

const UserAvatar = ({ userEmail, isSelf }) => {
  const users = useSelector((state) => state.users);
  const filteredUserInitials = useMemo(() => {
    const user = users?.find((u) => u.emailAddress === userEmail);
    if (isNil(user)) {
      return null;
    }
    const userName = user?.fullName;
    if (!userName) return "";
    const userNameInitials = userName
      .split(" ")
      .map((name) => name.charAt(0))
      .join("")
      .toUpperCase();
    return userNameInitials;
  }, []);

  const styles = useMemo(() => {
    const size = 24;
    return {
      mt: 0.5,
      // mt: 2.7,
      width: size,
      height: size,
      fontSize: `${size - 14}px`,

      backgroundColor: isSelf ? blueGrey[400] : grey[400],
    };
  }, []);

  return <Avatar sx={styles}>{filteredUserInitials}</Avatar>;
};

const CreatedTime = ({ children }) => {
  const theme = useTheme();
  if (isNil(children)) {
    return null;
  }
  return (
    <Txt
      body2
      sx={{
        fontWeight: 400,
        opacity: 0.6,
        color: theme.palette.text.secondary,
        fontSize: "10px",
      }}
    >
      {formatDateTime(children, "time")}
    </Txt>
  );
};

const CommentBody = ({
  children,
  isSelf,
  isException,
  topContent,
  attachmentsContent,
  editing = false,
  onChangeDraft,
  onSave,
  onCancel,
  saving = false,
}) => {
  const styles = useMemo(() => {
    const backgroundColor = isException
      ? orange[50]
      : isSelf
        ? "#e3f2fd78"
        : grey[100];
    const borderColor = isException
      ? orange[200]
      : isSelf
        ? blue[100]
        : grey[300];

    return {
      borderRadius: "14px",
      borderTopLeftRadius: isSelf ? "14px" : 0,
      borderTopRightRadius: isSelf ? 0 : "14px",
      py: 0.5,
      px: 1.5,
      pt: 1.5,
      backgroundColor,
      border: `1px solid ${borderColor}`,
      // maxHeight: "580px",
      // overflowY: "auto",
    };
  }, [isSelf, isException]);

  if (isNil(children)) {
    return null;
  }

  return (
    <Box sx={styles}>
      {topContent}
      {editing ? (
        <Box>
          <Box
            sx={{
              background: "#fff",
              border: `1px solid ${blue[200]}`,
              borderRadius: "8px",
              px: 1,
            }}
          >
            <HtmlEditor
              value={children}
              onChange={({ htmlString }) => onChangeDraft?.(htmlString)}
              placeholder="Edit your comment..."
            />
          </Box>
          <Flx ac g={0.5} sx={{ justifyContent: "flex-end", mt: 0.75 }}>
            <Button
              size="small"
              variant="text"
              onClick={onCancel}
              startIcon={<CloseOutlined sx={{ fontSize: 14 }} />}
              sx={{ fontSize: 12, py: 0.25 }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={onSave}
              disabled={saving}
              startIcon={
                saving ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <CheckOutlined sx={{ fontSize: 14 }} />
                )
              }
              sx={{ fontSize: 12, py: 0.25 }}
            >
              Save
            </Button>
          </Flx>
        </Box>
      ) : (
        <HtmlEditor
          value={children}
          readOnly
          placeholder="Start typing here..."
          // The bubble already provides padding (px/pt above); drop the
          // read-only editor's own content padding so text isn't double-inset.
          editorMainSx={{ "& .tiptap-prosemirror": { p: 0 } }}
        />
      )}
      {attachmentsContent}
    </Box>
  );
};

export default EnhancedCommentItem;
